<!-- Loading -->
@if (loading) {
  <div class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <div class="animate-spin text-4xl mb-4">⚙️</div>
      <p class="text-gray-500 dark:text-gray-400">Chargement du rapport…</p>
    </div>
  </div>
}

<!-- Error -->
@if (!loading && error) {
  <div class="max-w-2xl mx-auto mt-20 text-center">
    <p class="text-red-500 text-lg">{{ error }}</p>
    <button
      (click)="goBack()"
      class="mt-4 px-4 py-2 bg-gray-100 dark:bg-slate-800 rounded-lg text-sm"
    >
      ← Retour
    </button>
  </div>
}

<!-- Rapport -->
@if (!loading && rapport) {
  <div class="min-h-screen bg-gray-50 dark:bg-slate-900">
    <!-- ── Header gradient ── -->
    <div
      class="bg-gradient-to-br from-slate-800 to-slate-900 text-white px-6 py-8"
    >
      <div class="max-w-5xl mx-auto">
        <button
          (click)="goBack()"
          class="text-slate-300 hover:text-white text-sm flex items-center gap-1 mb-4 transition"
        >
          ← Retour aux événements
        </button>
        <div class="flex items-start justify-between gap-4">
          <div>
            <p class="text-slate-400 text-xs uppercase tracking-widest mb-1">
              Rapport post-événement
            </p>
            <h1 class="text-2xl md:text-3xl font-bold mb-1">
              {{ getEventIcon(rapport.event.eventType) }}
              {{ rapport.event.name }}
            </h1>
            <div
              class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-300 mt-2"
            >
              <span
                >📅
                {{
                  rapport.event.eventDate | date: "EEEE d MMMM yyyy" : "" : "fr"
                }}</span
              >
              @if (rapport.event.locationLabel || rapport.event.location) {
                <span
                  >📍
                  {{
                    rapport.event.locationLabel || rapport.event.location
                  }}</span
                >
              }
              @if (rapport.event.startTime) {
                <span
                  >⏰ {{ rapport.event.startTime
                  }}{{
                    rapport.event.endTime ? " – " + rapport.event.endTime : ""
                  }}{{
                    rapport.event.duration
                      ? " (" + rapport.event.duration + "h)"
                      : ""
                  }}</span
                >
              }
              @if (rapport.kpis.affluence) {
                <span>👥 Affluence : {{ rapport.kpis.affluence }}</span>
              }
              @if (rapport.event.locationRouteData?.distanceKm) {
                <span
                  >📏 {{ rapport.event.locationRouteData.distanceKm }} km — 🕐
                  {{
                    formatDuration(rapport.event.locationRouteData.durationMin)
                  }}</span
                >
              }
            </div>
          </div>
          <!-- Score global -->
          <div
            class="shrink-0 text-center bg-white dark:bg-slate-800/10 rounded-2xl px-6 py-4 backdrop-blur"
          >
            <div class="text-4xl font-black" [class]="scoreColor">
              {{ rapport.kpis.score }}
            </div>
            <div class="text-xs text-slate-300 mt-0.5">/ 100</div>
            <div class="text-xs font-semibold mt-1" [class]="scoreColor">
              {{ scoreLabel }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-5xl mx-auto px-4 py-8 space-y-6">
      <!-- ── KPI Cards ── -->
      <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
        <!-- Bénéfice Net Estimé -->
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-5 border border-gray-100 dark:border-slate-700">
          <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">
            Bénéfice Net Estimé
          </p>
          <p
            class="text-2xl font-bold"
            [class]="
              netProfitAfterFixed >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'
            "
          >
            {{ netProfitAfterFixed | number: "1.0-0" }} €
          </p>
          @if (deltaNetProfit > 0) {
            <p class="text-xs text-green-500 font-medium mt-1">
              +{{ deltaNetProfit | number: "1.0-0" }} € vs sans outil
            </p>
          }
          <p class="text-[10px] text-gray-400 mt-1">
            Détail dans le tableau ci-dessous ↓
          </p>
        </div>
        <!-- Réduction gaspillage -->
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-5 border border-gray-100 dark:border-slate-700">
          <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">
            Réduction gaspillage
          </p>
          <p class="text-2xl font-bold text-green-600 dark:text-green-400">
            +{{ wasteReduction | number: "1.0-0" }} €
          </p>
          <p class="text-xs text-gray-400 mt-1">
            vs ~{{ estimatedWasteWithoutKpi | number: "1.0-0" }} € sans outil
            ({{ rapport.kpis.wasteRate }}% réel vs ~{{ industryWasteRate }}%
            estimé)
          </p>
        </div>
        <!-- Réduction ventes manquées -->
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-5 border border-gray-100 dark:border-slate-700">
          <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">
            Réduction ventes manquées
          </p>
          @if (deltaMissedRevenueKpi > 0) {
            <p class="text-2xl font-bold text-green-600 dark:text-green-400">
              +{{ deltaMissedRevenueKpi | number: "1.0-0" }} €
            </p>
            <p class="text-xs text-gray-400 mt-1">
              vs ~{{ estimatedMissedRevenueKpi | number: "1.0-0" }} € sans outil
              (~{{ industryMissedRate }}% CA)
            </p>
          } @else {
            <p class="text-2xl font-bold text-gray-300">—</p>
            <p class="text-xs text-gray-400 mt-1">
              Estimé ~{{ estimatedMissedRevenueKpi | number: "1.0-0" }} € sans
              outil
            </p>
          }
          @if (rapport.kpis.totalRuptures > 0) {
            <p class="text-xs text-red-500 mt-1">
              ⚠️ {{ rapport.kpis.totalRuptures }} rupture(s) détectée(s)
            </p>
          } @else {
            <p class="text-xs text-green-600 dark:text-green-400 mt-1">✅ Aucune rupture</p>
          }
        </div>
      </div>

      <!-- ── Valeur SaaS ── -->
      <div class="mt-2"></div>
      <app-saas-gain-card
        [totalRevenue]="rapport.kpis.totalRevenue"
        [totalWaste]="rapport.kpis.totalWaste"
        [wasteRate]="rapport.kpis.wasteRate"
        [netProfit]="rapport.kpis.netProfit"
        [netRate]="rapport.kpis.netRate"
        [realProfitRate]="rapport.kpis.realProfitRate"
        [totalRealCost]="
          rapport.kpis.totalRealProfit !== null
            ? rapport.kpis.totalRevenue - rapport.kpis.totalRealProfit
            : null
        "
        [totalRuptures]="rapport.kpis.totalRuptures"
        [totalProducts]="rapport.products.length"
        [avgAccuracy]="rapport.kpis.avgAccuracy"
      />

      <!-- ── Produits ── -->
      @if (rapport.products.length > 0) {
        <div
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 border border-gray-100 dark:border-slate-700 overflow-hidden"
        >
          <div
            class="px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between"
          >
            <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-300">🛒 Produits</h2>
            @if (rapport.starProduct) {
              <span class="text-xs text-yellow-600 font-medium"
                >⭐ {{ rapport.starProduct.name }}</span
              >
            }
          </div>
          <div class="divide-y divide-gray-50">
            @for (p of rapport.products; track p.name) {
              <div
                class="px-4 py-2.5 flex items-center gap-2 flex-wrap"
                [class.bg-yellow-50]="
                  p.name === rapport.starProduct?.name ||
                  p.hadRupture ||
                  p.missedSales > 0
                "
              >
                <!-- Nom -->
                <span class="font-medium text-gray-900 dark:text-white text-sm shrink-0">{{
                  p.name
                }}</span>
                <!-- Périssabilité -->
                @if (p.perishability === "low") {
                  <span
                    class="text-[10px] bg-sky-100 text-sky-600 px-1.5 py-0.5 rounded-full shrink-0"
                    title="Non périssable — retour stock possible"
                    >🧊 Stock</span
                  >
                } @else if (p.perishability === "medium") {
                  <span
                    class="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-600 px-1.5 py-0.5 rounded-full shrink-0"
                    title="Périssabilité moyenne (1-2 jours)"
                    >⚠️ 1-2j</span
                  >
                }

                <!-- Badges -->
                @if (p.hadRupture) {
                  <span
                    class="text-[10px] bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded-full shrink-0"
                    >⚠️ Rupture</span
                  >
                } @else if (p.missedSales > 0) {
                  <span
                    class="text-[10px] bg-orange-100 dark:bg-orange-900/30 text-orange-600 px-1.5 py-0.5 rounded-full shrink-0"
                    >⚠️ Possible rupture</span
                  >
                } @else if (p.wasted === 0 && p.recommended > 0) {
                  <span
                    class="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded-full shrink-0"
                    >🎯 Zéro gaspi</span
                  >
                } @else if (p.sold === p.prepared && p.prepared > 0) {
                  <span
                    class="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded-full shrink-0"
                    >✅ Tout vendu</span
                  >
                }
                @if (p.recommended > 0 && p.prepared > p.recommended) {
                  <span class="text-[10px] text-orange-500 shrink-0"
                    >+{{ p.prepared - p.recommended }} surplus</span
                  >
                } @else if (p.recommended > 0 && p.prepared < p.recommended) {
                  <span class="text-[10px] text-blue-500 shrink-0"
                    >-{{ p.recommended - p.prepared }} sous reco</span
                  >
                }

                <!-- Séparateur -->
                <span class="text-gray-200 shrink-0">|</span>

                <!-- Barre + % -->
                <div class="flex items-center gap-1 shrink-0">
                  <div
                    class="w-14 h-1.5 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden"
                  >
                    <div
                      class="h-1.5 rounded-full"
                      [class]="sellRateColor(p.sellRate)"
                      [style.width.%]="p.sellRate"
                    ></div>
                  </div>
                  <span class="text-[11px] font-medium text-gray-500 dark:text-gray-400"
                    >{{ p.sellRate }}%</span
                  >
                </div>

                <!-- Chiffres -->
                @if (p.recommended > 0) {
                  <span class="text-[11px] text-purple-500 shrink-0"
                    >Reco <strong>{{ p.recommended }}</strong></span
                  >
                  <span class="text-[11px] text-gray-300">·</span>
                }
                <span class="text-[11px] text-gray-500 dark:text-gray-400 shrink-0"
                  >Préparé <strong>{{ p.prepared }}</strong></span
                >
                <span class="text-[11px] text-gray-300">·</span>
                <span
                  class="text-[11px] shrink-0"
                  [class]="p.hadRupture ? 'text-red-500' : 'text-green-600 dark:text-green-400'"
                  >Vendu <strong>{{ p.sold }}</strong></span
                >
                @if (p.wasted > 0) {
                  <span class="text-[11px] text-gray-300">·</span>
                  @if (p.perishability === "low") {
                    <span class="text-[11px] text-sky-500 shrink-0"
                      >Retour <strong>{{ p.wasted }}</strong></span
                    >
                  } @else {
                    <span class="text-[11px] text-orange-400 shrink-0"
                      >Gaspi <strong>{{ p.wasted }}</strong></span
                    >
                  }
                }

                <!-- Montants -->
                <div class="ml-auto flex items-center gap-2 shrink-0">
                  @if (p.realCost !== null) {
                    <span class="text-[11px] text-gray-400"
                      >Coût
                      <span class="font-semibold text-orange-500"
                        >{{ p.realCost | number: "1.0-0" }} €</span
                      ></span
                    >
                    <span class="text-gray-200">|</span>
                  }
                  <span class="font-bold text-gray-900 dark:text-white text-sm"
                    >CA
                    <span class="text-green-700 dark:text-green-300"
                      >{{ p.revenue | number: "1.0-0" }} €</span
                    ></span
                  >
                </div>
              </div>
            }
          </div>
          <!-- Ligne Totaux -->
          <div
            class="px-4 py-2.5 flex items-center gap-2 flex-wrap bg-gray-100 dark:bg-slate-800 border-t-2 border-gray-200 dark:border-slate-700 font-semibold text-sm text-gray-700 dark:text-gray-300"
          >
            <span class="shrink-0">📊 Totaux</span>
            <span class="text-gray-300 shrink-0">|</span>
            <span class="text-gray-500 dark:text-gray-400 shrink-0"
              >Préparé <strong>{{ totalPrepared }}</strong></span
            >
            <span class="text-gray-300">·</span>
            <span class="text-green-700 dark:text-green-300 shrink-0"
              >Vendu <strong>{{ totalSold }}</strong></span
            >
            @if (totalWasted > 0) {
              <span class="text-gray-300">·</span>
              <span class="text-orange-500 shrink-0"
                >Gaspi <strong>{{ totalWasted }}</strong></span
              >
            }
            <div class="ml-auto flex items-center gap-2 shrink-0">
              @if (rapport.kpis.totalRealProfit !== null) {
                <span class="text-[11px] text-gray-400"
                  >Coût
                  <span class="font-semibold text-orange-500"
                    >{{
                      rapport.kpis.totalRevenue - rapport.kpis.totalRealProfit
                        | number: "1.0-0"
                    }}
                    €</span
                  ></span
                >
                <span class="text-gray-300">|</span>
              }
              <span class="font-bold text-gray-900 dark:text-white"
                >CA
                <span class="text-green-700 dark:text-green-300"
                  >{{ rapport.kpis.totalRevenue | number: "1.0-0" }} €</span
                ></span
              >
            </div>
          </div>
        </div>
      }

      <!-- ── Notes terrain (ventes) ── -->
      @if (rapport.notes.length > 0) {
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 border border-gray-100 dark:border-slate-700 p-5">
          <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            📝 Notes terrain
          </h2>
          <ul class="space-y-2">
            @for (note of rapport.notes; track $index) {
              <li
                class="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-slate-900 rounded-lg px-4 py-2 italic"
              >
                "{{ note }}"
              </li>
            }
          </ul>
        </div>
      }

      <!-- ── Carte trajet + Météo + Notes événement ── -->
      <div class="grid md:grid-cols-2 gap-4">
        <!-- Carte trajet -->
        @if (rapport.event.locationCoordinates) {
          <div
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 border border-gray-100 dark:border-slate-700 overflow-hidden md:col-span-2"
          >
            <div
              class="px-5 py-3 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between"
            >
              <span
                class="font-semibold text-sm text-gray-800 dark:text-gray-100 flex items-center gap-2"
                >🗺️ Trajet depuis votre base</span
              >
              @if (rapport.event.locationRouteData?.distanceKm) {
                <span class="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-3">
                  <span
                    >📏
                    {{ rapport.event.locationRouteData.distanceKm }} km</span
                  >
                  <span
                    >🕐
                    {{
                      formatDuration(
                        rapport.event.locationRouteData.durationMin
                      )
                    }}</span
                  >
                </span>
              }
            </div>
            <div #routeMapContainer class="route-map-container"></div>
          </div>
        }

        <!-- Météo prévue -->
        @if (rapport.event.weatherCondition) {
          <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 border border-gray-100 dark:border-slate-700 p-5">
            <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              🌤️ Météo prévue
            </h2>
            <div class="flex items-center gap-4">
              <div class="text-5xl">
                {{ getWeatherIcon(rapport.event.weatherCondition) }}
              </div>
              <div>
                @if (rapport.event.temperature) {
                  <div class="text-2xl font-bold text-gray-900 dark:text-white">
                    {{ rapport.event.temperature }}°C
                  </div>
                }
                <div class="text-gray-600 dark:text-gray-400 capitalize text-sm">
                  {{ rapport.event.weatherCondition.replace("_", " ") }}
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Notes de l'événement -->
        @if (rapport.event.notes) {
          <div
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 border border-gray-100 dark:border-slate-700 p-5"
            [class.md:col-span-2]="!rapport.event.weatherCondition"
          >
            <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              📋 Notes événement
            </h2>
            <p class="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
              {{ rapport.event.notes }}
            </p>
          </div>
        }
      </div>

      <!-- ── Footer actions ── -->
      <div class="flex flex-wrap gap-3 pt-2 pb-8">
        <button
          (click)="goBack()"
          class="px-5 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition text-sm"
        >
          ← Retour
        </button>
        @if (rapport.salesIds?.length > 0) {
          <button
            (click)="editSales()"
            class="px-5 py-2.5 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition text-sm flex items-center gap-2"
          >
            ✏️ Corriger les ventes
          </button>
        }
        <a
          [routerLink]="['/events', rapport.event.id]"
          class="px-5 py-2.5 bg-slate-800 text-white font-medium rounded-lg hover:bg-slate-700 transition text-sm"
        >
          Voir la fiche événement →
        </a>
      </div>
    </div>
  </div>
}
